// Pure constants, copy and row helpers for the deleted-folders report (Phase 5).
// No import declarations on purpose: Node loads this file directly in the phase checks.

export const REPORT_USER_TYPES = ['admin', 'interno']
export const PAGE_SIZE = 50
export const PAGE_TITLE = 'Report cartelle eliminate'
export const BREADCRUMB_ITEMS = [
  { text: 'Report', disabled: true },
  { text: 'Cartelle eliminate', active: true },
]
export const DATE_FORMAT = 'DD/MM/YYYY HH:mm'
export const EM_DASH = '—'
export const TABLE_FIELDS = [
  { key: 'label', label: 'Nome' },
  { key: 'deleted_at', label: 'Eliminata il' },
  { key: 'causer_username', label: 'Eliminata da' },
  { key: 'deletion_type', label: 'Tipo' },
  { key: 'actions', label: 'Azioni' },
]
export const TYPE_BADGES = {
  manual: { text: 'Manuale', variant: 'info' },
  automatic_expiration: { text: 'Scadenza automatica', variant: 'warning' },
}
export const EMPTY_TEXT = 'Nessuna cartella eliminata nel periodo selezionato'
export const PERMANENT_LABEL = 'Eliminata definitivamente'
export const RETENTION_TOOLTIP = 'Voce ricostruita dallo storico: recuperabile per 5 anni dalla data di eliminazione.'
export const LOAD_ERROR_OVERRIDES = {
  403: 'Non hai il permesso di accedere a questa pagina.',
}

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key)

const isPlainObject = value => typeof value === 'object' && value !== null && !Array.isArray(value)

// The rows of a successful reply (data.data.objects), or null for any other shape.
// Nothing is dropped or reordered: a full page must mean exactly PAGE_SIZE rows.
export const readReportRows = response => {
  if (!isPlainObject(response) || !isPlainObject(response.data)) {
    return null
  }
  const payload = response.data
  if (Number(payload.code) !== 1 || !isPlainObject(payload.data)) {
    return null
  }
  const { objects } = payload.data
  if (!Array.isArray(objects) || !objects.every(isPlainObject)) {
    return null
  }
  return objects
}

export const getCauserText = row => {
  const name = isPlainObject(row) ? row.causer_username : null
  return typeof name === 'string' && name.trim() !== '' ? name : EM_DASH
}

export const getTypeBadge = row => {
  const type = isPlainObject(row) ? row.deletion_type : null
  if (typeof type === 'string' && hasOwn(TYPE_BADGES, type)) {
    return { ...TYPE_BADGES[type] }
  }
  return null
}

// An activity_log label may be null: the row still gets a visible cell.
export const getLabelText = row => {
  const label = isPlainObject(row) ? row.label : null
  return typeof label === 'string' && label.trim() !== '' ? label : EM_DASH
}

// Rows rebuilt from the audit history (source activity_log) can no longer be restored.
export const isPermanentlyDeleted = row => isPlainObject(row) && row.source === 'activity_log'

// panel: show the page-level error panel. forbidden: a 403, so the table is hidden.
// Any other failure keeps the rows already on screen and shows the panel only when nothing was loaded.
export const getLoadFailure = (status, hasLoaded) => {
  if (status === 403) {
    return { panel: true, forbidden: true }
  }
  return { panel: !hasLoaded, forbidden: false }
}

// --- Filter form and pager (plan 05-02) --------------------------------------------------

export const DEFAULT_RANGE_DAYS = 30
export const DATE_RANGE_ERROR_TEXT = 'La data di fine non può essere prima della data di inizio.'
export const DATE_FROM_INVALID_TEXT = 'La data di inizio non è valida.'
export const DATE_TO_INVALID_TEXT = 'La data di fine non è valida.'
// Same Italian labels as the expiration modal; camelCase keys reach the b-form-datepicker props through v-bind.
export const DATEPICKER_PROPS = {
  closeButton: true,
  dateFormatOptions: { year: 'numeric', month: '2-digit', day: '2-digit' },
  labelCalendar: 'Calendario',
  labelCloseButton: 'Chiudi',
  labelCurrentMonth: 'Mese corrente',
  labelHelp: 'Usa le frecce per scegliere una data',
  labelNextMonth: 'Mese successivo',
  labelNextYear: 'Anno successivo',
  labelNoDateSelected: 'Nessuna data selezionata',
  labelPrevMonth: 'Mese precedente',
  labelPrevYear: 'Anno precedente',
  labelSelected: 'Data selezionata',
  labelToday: 'Oggi',
  locale: 'it',
  startWeekday: 1,
}

const pad = (value, length) => String(value).padStart(length, '0')

const formatUtcDay = time => {
  const day = new Date(time)
  return `${pad(day.getUTCFullYear(), 4)}-${pad(day.getUTCMonth() + 1, 2)}-${pad(day.getUTCDate(), 2)}`
}

const YMD_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/

// True only for a string YYYY-MM-DD that is a real calendar day.
export const isYmd = value => {
  if (typeof value !== 'string') {
    return false
  }
  const match = YMD_PATTERN.exec(value)
  if (!match) {
    return false
  }
  const time = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return formatUtcDay(time) === value
}

// The last DEFAULT_RANGE_DAYS calendar days ending today: local year, month and day, then UTC arithmetic,
// so a DST switch day neither skips nor repeats a day.
export const getDefaultRange = (now = new Date()) => {
  const year = now.getFullYear()
  const month = now.getMonth()
  const day = now.getDate()
  return {
    dateFrom: formatUtcDay(Date.UTC(year, month, day - DEFAULT_RANGE_DAYS)),
    dateTo: formatUtcDay(Date.UTC(year, month, day)),
  }
}

const isBlank = value => value === '' || value === null || value === undefined

// Empty dates are absent (the server accepts them); malformed or reversed dates are refused.
export const validateRange = (dateFrom, dateTo) => {
  if (!isBlank(dateFrom) && !isYmd(dateFrom)) {
    return { valid: false, field: 'dateFrom', text: DATE_FROM_INVALID_TEXT }
  }
  if (!isBlank(dateTo) && !isYmd(dateTo)) {
    return { valid: false, field: 'dateTo', text: DATE_TO_INVALID_TEXT }
  }
  // Fixed-width YYYY-MM-DD strings compare correctly as text; both bounds are inclusive.
  if (!isBlank(dateFrom) && !isBlank(dateTo) && dateTo < dateFrom) {
    return { valid: false, field: 'dateTo', text: DATE_RANGE_ERROR_TEXT }
  }
  return { valid: true, field: null, text: null }
}

const isValidOffset = value => typeof value === 'number' && Number.isSafeInteger(value) && value >= 0

// Only real YYYY-MM-DD dates are ever sent; an absent or invalid date is omitted.
export const buildParams = (filters, offset) => {
  const source = isPlainObject(filters) ? filters : {}
  const params = {}
  if (isYmd(source.dateFrom)) {
    params.date_from = source.dateFrom
  }
  if (isYmd(source.dateTo)) {
    params.date_to = source.dateTo
  }
  params.offset = isValidOffset(offset) ? offset : 0
  params.limit = PAGE_SIZE
  return params
}

// The API returns no total: only a full page can have a successor.
export const canGoNext = rowsLength => rowsLength === PAGE_SIZE

export const getNextOffset = offset => offset + PAGE_SIZE

export const getPrevOffset = offset => Math.max(0, offset - PAGE_SIZE)

export const getPageNumber = offset => (isValidOffset(offset) ? Math.floor(offset / PAGE_SIZE) + 1 : 1)

// An empty page above offset 0 (its last row was removed) steps back one page; otherwise null.
export const getStepBackOffset = (offset, rowsLength) => (rowsLength === 0 && offset > 0 ? getPrevOffset(offset) : null)

// Maps a 422 field map to the fixed Italian texts; the backend text is never shown.
export const getRangeFieldErrors = fieldErrors => {
  const errors = { dateFrom: null, dateTo: null }
  if (!isPlainObject(fieldErrors)) {
    return errors
  }
  if (Array.isArray(fieldErrors.date_from)) {
    errors.dateFrom = DATE_FROM_INVALID_TEXT
  }
  if (Array.isArray(fieldErrors.date_to)) {
    errors.dateTo = DATE_RANGE_ERROR_TEXT
  }
  return errors
}

const hasText = value => typeof value === 'string' && value !== ''

export const hasRangeError = errors => isPlainObject(errors) && (hasText(errors.dateFrom) || hasText(errors.dateTo))

// --- Restore (plan 05-04) ------------------------------------------------------------------

export const RESTORE_CONFIRM = { title: 'Ripristina cartella?', okTitle: 'OK', cancelTitle: 'Annulla' }
export const RESTORE_SUCCESS_TEXT = 'Cartella ripristinata.'
export const RESTORE_DESTINATION_MODAL_ID = 'report-restore-modal'
export const RESTORE_CHOOSE_HINT = 'La cartella era al primo livello: scegli dove ripristinarla.'

const DIGITS_PATTERN = /^[1-9]\d*$/

// A parent id the backend rule 'required|numeric|exists' can accept: a positive integer, as number or digit string.
export const isValidParentId = value => {
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) && value > 0
  }
  return typeof value === 'string' && DIGITS_PATTERN.test(value)
}

// original: restore into the recorded parent. choose: the user picks the destination. none: no restore control.
export const getRestoreMode = row => {
  if (!isPlainObject(row) || row.recoverable !== true || isPermanentlyDeleted(row) || !isValidParentId(row.id)) {
    return 'none'
  }
  return isValidParentId(row.father_id) ? 'original' : 'choose'
}

// The only builder of the restore body: it never yields a missing or null parent.
export const buildRestoreBody = row => (getRestoreMode(row) === 'original' ? { father_id: row.father_id } : null)

export const buildRestoreConfirmMessage = label => {
  const base = 'La cartella sarà ripristinata nella sua posizione originale'
  return typeof label === 'string' && label.trim() !== '' ? `${base}: ${label}.` : `${base}.`
}

export default {
  REPORT_USER_TYPES,
  PAGE_SIZE,
  PAGE_TITLE,
  BREADCRUMB_ITEMS,
  DATE_FORMAT,
  EM_DASH,
  TABLE_FIELDS,
  TYPE_BADGES,
  EMPTY_TEXT,
  PERMANENT_LABEL,
  RETENTION_TOOLTIP,
  LOAD_ERROR_OVERRIDES,
  readReportRows,
  getCauserText,
  getTypeBadge,
  getLabelText,
  isPermanentlyDeleted,
  getLoadFailure,
  DEFAULT_RANGE_DAYS,
  DATE_RANGE_ERROR_TEXT,
  DATE_FROM_INVALID_TEXT,
  DATE_TO_INVALID_TEXT,
  DATEPICKER_PROPS,
  isYmd,
  getDefaultRange,
  validateRange,
  buildParams,
  canGoNext,
  getNextOffset,
  getPrevOffset,
  getPageNumber,
  getStepBackOffset,
  getRangeFieldErrors,
  hasRangeError,
  RESTORE_CONFIRM,
  RESTORE_SUCCESS_TEXT,
  RESTORE_DESTINATION_MODAL_ID,
  RESTORE_CHOOSE_HINT,
  isValidParentId,
  getRestoreMode,
  buildRestoreBody,
  buildRestoreConfirmMessage,
}
